// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';

import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import Dialog from 'presentations/views/components/atoms/Dialog';
import TextField from 'presentations/views/components/atoms/TextField';
import Icon from 'presentations/views/components/atoms/Icon';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import FormLabel from 'presentations/views/components/molecules/FormLabel';
import { blueGrayPale } from 'presentations/views/styles/color';
import { useOnetimeLinkDialog } from 'presentations/views/components/organisms/OnetimeLinkDialog/hooks';
import Link from 'presentations/views/components/atoms/Link';
import { ViewerItem } from 'presentations/api/schema/user';
import { EventTypeListItem } from 'presentations/api/schema/event_type';
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { helpPath } from 'presentations/routes/path';

interface Props {
  viewer: ViewerItem;
  eventType: EventTypeListItem;
  onClose?: any;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = useOnetimeLinkDialog(props);

  return (
    <StyledDialog
      isOpen
      onClose={props.onClose}
      title={t('title')}
      actions={
        <ButtonContainer>
          <DialogSubmitField
            isLoading={state.isLoading}
            onSubmit={actions.onSubmit}
            submitLabel={t('Submit')}
            isDisabled={!state.onetimeLinkUrl}
          />
        </ButtonContainer>
      }
      disableMinWidth
      titleBottomMargin={'0'}
      scroll
    >
      <DialogBody>
        <Block>
          <Message>
            {t('description.0')}
            {/* TODO: help URL置き換え */}
            <Link href={helpPath('7155176')} target="_blank">
              {t('description.1')}
              <Icon
                name="checkbox-multiple-blank-outline"
                size={fontSize(14)}
              />
            </Link>
          </Message>
        </Block>
        <OnetimeLinkContainer>
          <OnetimeLinkTitle>URL</OnetimeLinkTitle>
          {state.onetimeLinkUrl ? state.onetimeLinkUrl : <Skeleton />}
        </OnetimeLinkContainer>
        <Block>
          <FormLabel isNotRequired disableContainerMargin>
            {t('name')}
          </FormLabel>
          <FormDescription>{t('name description')}</FormDescription>
          <FormField>
            <StyledTextField
              name={'name'}
              defaultValue={props.eventType.name}
              value={state.name}
              isFullWidth={true}
              placeholder={t('name placeholder')}
              onChange={actions.onChange}
              isDisabled={state.isLoading}
            />
          </FormField>
        </Block>
      </DialogBody>
    </StyledDialog>
  );
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    max-width: 540px;
    overflow-y: visible !important;
  }
`;

const ButtonContainer = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: center;
`;

const DialogBody = styled.div`
  margin: ${spacing(8)} ${spacing(4)};
`;

const Block = styled.div`
  margin-bottom: ${spacing(24)};
  :last-of-type {
    margin-bottom: 0;
  }

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(14)};
  }
`;

const Message = styled.div`
  margin-top: ${spacing(16)};
  white-space: pre-wrap;
  font-size: ${fontSize(14)};
  font-weight: initial;

  ${mediaMax.tablet} {
    margin-top: ${spacing(8)};
    font-size: ${fontSize(12)};
  }
`;

const OnetimeLinkContainer = styled.div`
  padding: ${spacing(16)};
  margin-bottom: ${spacing(16)};
  background-color: ${blueGrayPale};
  border-radius: ${borderRadius(12)};
  font-size: ${fontSize(14)};
  word-break: break-all;

  ${mediaMax.tablet} {
    padding: ${spacing(14)};
    font-size: ${fontSize(12)};
  }
`;
const OnetimeLinkTitle = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(6)};
  }
`;

const FormDescription = styled.div`
  margin: ${spacing(8)} 0;
  font-size: ${fontSize(14)};

  ${mediaMax.tablet} {
    margin: ${spacing(14)} 0;
    font-size: ${fontSize(12)};
  }
`;
const FormField = styled.div``;
const StyledTextField = styled(TextField)`
  flex: 1;
`;
